(() => {
  const elementName = 'media-gallery-carousel';

  if (customElements.get(elementName)) return;

  class MediaGalleryCarousel extends HTMLElement {
    connectedCallback() {
      this.initFrame = requestAnimationFrame(() => this.initialize());
    }

    initialize() {
      this.cleanup();

      this.track = this.querySelector('[data-mg-track]');
      if (!this.track) return;

      this.controller = new AbortController();
      this.signal = this.controller.signal;
      this.motionQuery = matchMedia('(prefers-reduced-motion: reduce)');

      this.prevButton = this.querySelector('[data-mg-prev]');
      this.nextButton = this.querySelector('[data-mg-next]');
      this.pauseButton = this.querySelector('[data-mg-pause]');

      this.autoEnabled = this.dataset.autoScroll === 'true';
      this.interval = Math.max(
        3,
        Math.min(15, Number(this.dataset.autoInterval) || 5)
      ) * 1000;

      this.userPaused = false;
      this.hovered = false;
      this.pointerDown = false;
      this.visible = false;
      this.activeEmbed = null;
      this.scrollFrame = null;
      this.loadingGroup = false;
      this.groupReady = this.dataset.groupReady === 'true';

      this.listen(this.prevButton, 'click', () => {
        this.advance(-1);
        this.schedule();
      });

      this.listen(this.nextButton, 'click', () => {
        this.advance(1);
        this.schedule();
      });

      this.listen(this.pauseButton, 'click', () => {
        this.userPaused = !this.userPaused;
        this.updatePauseButton();
        this.schedule();
      });

      this.listen(this, 'mouseenter', () => {
        if (!matchMedia('(hover: hover)').matches) return;
        this.hovered = true;
        this.schedule();
      });

      this.listen(this, 'mouseleave', () => {
        this.hovered = false;
        this.schedule();
      });

      this.listen(this, 'focusin', () => this.schedule());

      this.listen(this, 'focusout', () => {
        queueMicrotask(() => {
          if (!this.signal.aborted) this.schedule();
        });
      });

      this.listen(this, 'pointerdown', () => {
        this.lastInteractionWasPointer = true;
        this.pointerDown = true;
        this.schedule();
      });

      this.listen(this, 'keydown', () => {
        this.lastInteractionWasPointer = false;
      });

      const endPointer = () => {
        this.pointerDown = false;
        this.schedule();
      };

      this.listen(window, 'pointerup', endPointer);
      this.listen(window, 'pointercancel', endPointer);

      this.listen(this.track, 'wheel', () => this.schedule(), {
        passive: true
      });

      this.listen(this.track, 'scroll', () => {
        if (this.scrollFrame) return;

        this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = null;
          this.updateNavigation();
          this.pauseOffscreenMedia();
          this.schedule();
        });
      }, { passive: true });

      this.listen(this.track, 'keydown', event => {
        if (event.target !== this.track) return;

        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.advance(1);
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.advance(-1);
        }
      });

      this.listen(document, 'visibilitychange', () => {
        if (document.hidden) this.stopMedia();
        this.schedule();
      });

      this.listen(this.motionQuery, 'change', () => {
        this.updatePauseButton();
        this.schedule();
      });

      this.setupDelegatedControls();
      this.setupVideoEvents();
      this.setupEditorEvents();

      this.dialogs.forEach(dialog => this.bindDialog(dialog));

      if (this.dataset.source !== 'metaobject_group') {
        this.prepareManualPhotos();
      }

      this.resizeObserver = new ResizeObserver(() => {
        this.updateNavigation();
        this.schedule();
      });

      this.resizeObserver.observe(this.track);

      this.visibilityObserver = new IntersectionObserver(entries => {
        this.visible = Boolean(entries[0]?.isIntersecting);

        if (!this.visible) this.stopMedia();

        this.schedule();
      });

      this.visibilityObserver.observe(this);

      if (
        this.dataset.source === 'metaobject_group' &&
        !this.groupReady
      ) {
        this.loadObserver = new IntersectionObserver(entries => {
          if (!entries.some(entry => entry.isIntersecting)) return;

          this.loadObserver.disconnect();
          this.loadGroup();
        }, { rootMargin: '400px 0px' });

        this.loadObserver.observe(this);
      }

      this.updateNavigation();
      this.updatePauseButton();
      this.schedule();
    }

    listen(target, type, callback, options = {}) {
      if (!target) return;

      target.addEventListener(type, callback, {
        ...options,
        signal: this.signal
      });
    }

    get dialogs() {
      return Array.from(this.querySelectorAll('[data-mg-dialog]'));
    }

    get slides() {
      return Array.from(
        this.track.querySelectorAll('[data-mg-slide]')
      ).filter(slide => !slide.hidden);
    }

    get behavior() {
      return this.motionQuery.matches ? 'auto' : 'smooth';
    }

    get hasOverflow() {
      return this.track.scrollWidth > this.track.clientWidth + 2;
    }

    get popupOpen() {
      return this.dialogs.some(dialog => dialog.open);
    }

    get videoPlaying() {
      return Array.from(this.querySelectorAll('video')).some(
        video => !video.paused && !video.ended
      );
    }

    normalize(value) {
      return String(value || '').trim().toLowerCase();
    }

    safeUrl(value) {
      if (!value) return '';

      try {
        const url = new URL(value, location.href);
        return url.protocol === 'https:' ? url.href : '';
      } catch (_) {
        return '';
      }
    }

    youtubeId(value) {
      try {
        const url = new URL(value);
        const host = url.hostname.toLowerCase();
        let id = '';

        if (host === 'youtu.be' || host === 'www.youtu.be') {
          id = url.pathname.split('/').filter(Boolean)[0] || '';
        } else if ([
          'youtube.com',
          'www.youtube.com',
          'm.youtube.com',
          'youtube-nocookie.com',
          'www.youtube-nocookie.com'
        ].includes(host)) {
          const parts = url.pathname.split('/').filter(Boolean);

          if (parts[0] === 'watch') {
            id = url.searchParams.get('v') || '';
          } else if (['embed', 'shorts'].includes(parts[0])) {
            id = parts[1] || '';
          }
        }

        return /^[A-Za-z0-9_-]{11}$/.test(id) ? id : '';
      } catch (_) {
        return '';
      }
    }

    node(tag, className, text) {
      const element = document.createElement(tag);

      if (className) element.className = className;
      if (text !== undefined) element.textContent = text;

      return element;
    }

    image(url, alt, className) {
      const src = this.safeUrl(url);
      if (!src) return null;

      const image = this.node('img', className);
      image.src = src;
      image.alt = alt || '';
      image.loading = 'lazy';
      image.decoding = 'async';

      return image;
    }

    playIcon() {
      const icon = this.node('span', 'media-gallery__play-icon', '▶');
      icon.setAttribute('aria-hidden', 'true');
      return icon;
    }

    appendRichText(container, html) {
      // Rebuild approved rich-text elements without copying event handlers.
      const parsed = new DOMParser().parseFromString(
        String(html || ''),
        'text/html'
      );

      const allowed = new Set([
        'P', 'DIV', 'BR', 'STRONG', 'B', 'EM', 'I',
        'U', 'S', 'UL', 'OL', 'LI', 'A', 'SPAN',
        'H1', 'H2', 'H3', 'H4', 'H5', 'H6'
      ]);

      const blocked = new Set([
        'SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED'
      ]);

      const copy = (source, target) => {
        source.childNodes.forEach(child => {
          if (child.nodeType === Node.TEXT_NODE) {
            target.appendChild(document.createTextNode(child.textContent));
            return;
          }

          if (child.nodeType !== Node.ELEMENT_NODE) return;
          if (blocked.has(child.tagName)) return;

          if (!allowed.has(child.tagName)) {
            copy(child, target);
            return;
          }

          const element = document.createElement(
            child.tagName.toLowerCase()
          );

          if (child.tagName === 'A') {
            const href = this.safeUrl(child.getAttribute('href'));

            if (href) {
              element.href = href;

              if (child.getAttribute('target') === '_blank') {
                element.target = '_blank';
                element.rel = 'noopener noreferrer';
              }
            }
          }

          copy(child, element);
          target.appendChild(element);
        });
      };

      copy(parsed.body, container);
    }

    async loadGroup() {
      if (this.loadingGroup || this.groupReady) return;

      const signal = this.signal;
      const group = this.normalize(this.dataset.galleryGroup);

      if (!group) {
        this.showStatus('Enter a Gallery group in the section settings.');
        return;
      }

      this.loadingGroup = true;
      this.setAttribute('aria-busy', 'true');
      this.showStatus('Loading gallery…');

      try {
        const entries = await this.fetchCandidates(signal);
        if (signal.aborted) return;

        const seen = new Set();

        const candidates = entries.filter(entry => {
          if (this.normalize(entry.group) !== group) return false;
          if (!entry.id || seen.has(entry.id)) return false;

          let usable = false;

          if (entry.media_type === 'image') {
            usable = Boolean(this.safeUrl(entry.image_url));
          } else if (entry.media_type === 'video') {
            usable = Array.isArray(entry.video_sources) &&
              entry.video_sources.some(source => this.safeUrl(source.url));
          } else if (entry.media_type === 'external_video') {
            usable = Boolean(this.safeUrl(entry.external_url));
          }

          if (usable) seen.add(entry.id);
          return usable;
        });

        const featured = [];
        const others = [];

        candidates.forEach((entry, index) => {
          const value = entry.featured_position;
          const position = value === null || value === undefined ||
            String(value).trim() === '' ? null : Number(value);

          const item = { entry, index, position };

          if (
            Number.isInteger(position) &&
            position >= 1 &&
            position <= 5
          ) {
            featured.push(item);
          } else {
            others.push(item);
          }
        });

        featured.sort((a, b) =>
          a.position - b.position || a.index - b.index
        );

        const opening = featured.slice(0, 5);
        const pool = [...featured.slice(5), ...others].sort(
          (a, b) => a.index - b.index
        );

        if (this.dataset.shuffleGroup === 'true') {
          for (let i = pool.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [pool[i], pool[j]] = [pool[j], pool[i]];
          }
        }

        const limit = Math.max(
          1,
          Math.min(30, Number(this.dataset.slideLimit) || 15)
        );

        const selected = [...opening, ...pool]
          .slice(0, limit)
          .map(item => item.entry);

        this.renderGroup(selected);
        this.groupReady = true;
        this.dataset.groupReady = 'true';

        this.showStatus(
          selected.length ? '' : 'No usable media entries match this gallery group.'
        );

        if (this.dataset.designMode === 'true') {
          const positions = featured.map(item => item.position);
          const duplicatePositions = positions.filter(
            (position, index) => positions.indexOf(position) !== index
          );

          if (duplicatePositions.length) {
            console.warn(
              'Media gallery: duplicate Featured Position values.',
              [...new Set(duplicatePositions)],
              'Equal positions retain source order.'
            );
          }
        }
      } catch (error) {
        if (signal.aborted) return;

        console.error('Media gallery source:', error);
        this.showStatus('The gallery could not load. Please reload to try again.');
      } finally {
        if (!signal.aborted) {
          this.loadingGroup = false;
          this.removeAttribute('aria-busy');
          this.updateNavigation();
          this.schedule();
        }
      }
    }

    async fetchCandidates(signal) {
      const sourceId = this.dataset.sourceSection || 'media-gallery-source';
      const base = new URL(location.href);

      base.hash = '';
      base.searchParams.delete('sections');
      base.searchParams.delete('page');
      base.searchParams.set('section_id', sourceId);

      let url = new URL(base);
      let expectedPage = 1;
      const entries = [];
      const visited = new Set();

      while (url) {
        if (visited.has(url.href)) {
          throw new Error('Repeated gallery pagination URL.');
        }

        visited.add(url.href);

        const response = await fetch(url, {
          credentials: 'same-origin',
          signal
        });

        if (!response.ok) {
          throw new Error(`Gallery source returned HTTP ${response.status}.`);
        }

        const html = await response.text();
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const payload = parsed.querySelector('[data-mg-source-data]');

        if (!payload) {
          throw new Error('Gallery source data was not found.');
        }

        const data = JSON.parse(payload.textContent);

        if (data.available !== true || !Array.isArray(data.entries)) {
          throw new Error('Gallery source is unavailable or malformed.');
        }

        const page = Number(data.page);
        const pages = Number(data.pages);
        const total = Number(data.total_entries);

        if (
          !Number.isInteger(page) ||
          !Number.isInteger(pages) ||
          page !== expectedPage ||
          pages < page
        ) {
          throw new Error('Unexpected gallery pagination response.');
        }

        // Do not silently claim a complete pool beyond Liquid's limit.
        if (total > 25000 || pages > 500) {
          throw new Error(
            'This library exceeds the current gallery source retrieval limit.'
          );
        }

        entries.push(...data.entries);

        if (page >= pages) {
          url = null;
          continue;
        }

        if (!data.next_url) {
          throw new Error('The source omitted its next-page URL.');
        }

        const next = new URL(data.next_url, base);
        const pageParameter = data.page_parameter || 'page';
        const nextValue = next.searchParams.get(pageParameter);

        if (!nextValue) {
          throw new Error('The next-page parameter was not found.');
        }

        // Keep the current preview/theme context on every request.
        url = new URL(base);
        url.searchParams.set(pageParameter, nextValue);
        expectedPage += 1;
      }

      return entries;
    }

    showStatus(message) {
      let status = this.querySelector('[data-mg-source-status]');

      if (!status && message) {
        status = this.node('div', 'media-gallery__empty');
        status.dataset.mgSourceStatus = '';
        status.setAttribute('role', 'status');

        const container = this.querySelector('.media-gallery__container');
        (container || this).appendChild(status);
      }

      if (status) {
        status.textContent = message;
        status.hidden = !message;
      }
    }

    renderGroup(entries) {
      this.stopMedia();

      this.querySelectorAll('[data-mg-generated-dialog]').forEach(
        dialog => dialog.remove()
      );

      const fragment = document.createDocumentFragment();

      entries.forEach((entry, index) => {
        const slide = this.node('li', 'media-gallery__slide');
        slide.dataset.mgSlide = '';
        slide.dataset.entryId = entry.id;

        const card = this.node('div', 'media-gallery__card');
        const media = this.node('div', 'media-gallery__media');
        media.dataset.mgMedia = '';

        if (entry.media_type === 'image') {
          const trigger = this.node('button', 'media-gallery__trigger');
          trigger.type = 'button';
          trigger.dataset.mgPhotoOpen = '';
          trigger.dataset.mgPhotoUrl = this.safeUrl(entry.image_url);
          trigger.dataset.mgPhotoHideTitle =
            entry.source === 'page_file' ? 'true' : 'false';
          trigger.setAttribute(
            'aria-label',
            entry.title || entry.image_alt || 'Enlarge photo'
          );

          const image = this.image(
            entry.thumbnail_url || entry.image_url,
            entry.image_alt || entry.title,
            'media-gallery__image'
          );

          if (image) trigger.appendChild(image);
          media.appendChild(trigger);
        } else {
          const external = this.safeUrl(entry.external_url);
          const youtube = this.youtubeId(external);

          const canPopup = entry.media_type === 'video' || Boolean(youtube);
          const trigger = this.node(
            canPopup ? 'button' : 'a',
            'media-gallery__trigger'
          );

          const label = entry.title || this.dataset.playLabel || 'Play video';
          trigger.setAttribute('aria-label', label);

          if (canPopup) {
            trigger.type = 'button';

            const id = `${this.id}-GroupVideo-${index}`;
            trigger.dataset.mgPopupOpen = id;
            trigger.setAttribute('aria-haspopup', 'dialog');
            trigger.setAttribute('aria-controls', id);

            const dialog = this.createVideoDialog(entry, id, youtube);
            this.appendChild(dialog);
            this.bindDialog(dialog);
          } else {
            trigger.href = external;
            trigger.target = '_blank';
            trigger.rel = 'noopener noreferrer';
          }

          const image = this.image(
            entry.thumbnail_url,
            entry.title,
            'media-gallery__image'
          );

          if (image) trigger.appendChild(image);

          trigger.appendChild(this.playIcon());
          media.appendChild(trigger);
        }

        card.appendChild(media);

        if (entry.title) {
          const info = this.node('div', 'media-gallery__info');
          info.appendChild(this.node(
            'h3',
            'media-gallery__title',
            entry.title
          ));
          card.appendChild(info);
        }

        slide.appendChild(card);
        fragment.appendChild(slide);
      });

      this.track.replaceChildren(fragment);

      if (entries.length) {
        this.appendFullGalleryCard();
      }

      this.style.setProperty(
        '--mg-slide-count',
        Math.max(1, this.slides.length)
      );

      this.track.scrollLeft = 0;

      this.updateNavigation();
    }

    appendFullGalleryCard() {
      // The grid inherits renderGroup(), but must not get this card.
      if (this.localName !== 'media-gallery-carousel') return;

      const template = this.querySelector(
        'template[data-mg-full-gallery-card]'
      );

      if (!template) return;

      const card = template.content.firstElementChild;
      if (!card) return;

      this.track.appendChild(card.cloneNode(true));
    }

    createDialog(id, generated = true) {
      const dialog = this.node('dialog', 'media-gallery-popup');
      dialog.id = id;
      dialog.dataset.mgDialog = '';

      if (generated) dialog.dataset.mgGeneratedDialog = '';
      dialog.style.cssText = this.dataset.popupStyle || '';

      const content = this.node('div', 'media-gallery-popup__content');
      const toolbar = this.node('div', 'media-gallery-popup__toolbar');
      const close = this.node('button', 'media-gallery-popup__close', '×');

      close.type = 'button';
      close.dataset.mgPopupClose = '';
      close.setAttribute(
        'aria-label',
        this.dataset.closeLabel || 'Close'
      );

      toolbar.appendChild(close);
      content.appendChild(toolbar);
      dialog.appendChild(content);

      return dialog;
    }

    createVideoDialog(entry, id, youtube) {
      const dialog = this.createDialog(id);
      const content = dialog.querySelector('.media-gallery-popup__content');
      const titleText = entry.title || this.dataset.playLabel || 'Video';
      const title = this.node(
        'h2',
        'media-gallery-popup__title',
        titleText
      );

      title.id = `${id}-Title`;
      dialog.setAttribute('aria-labelledby', title.id);
      content.appendChild(title);

      if (entry.description_html) {
        const description = this.node(
          'div',
          'media-gallery-popup__description'
        );

        this.appendRichText(description, entry.description_html);
        content.appendChild(description);
      }

      if (entry.media_type === 'video') {
        const video = this.node('video', 'media-gallery-popup__video');
        video.controls = true;
        video.playsInline = true;
        video.preload = 'none';

        const poster = this.safeUrl(entry.thumbnail_url);
        if (poster) video.poster = poster;

        entry.video_sources.forEach(item => {
          const url = this.safeUrl(item.url);
          if (!url) return;

          const source = document.createElement('source');
          source.dataset.src = url;

          if (item.mime_type) source.type = item.mime_type;
          video.appendChild(source);
        });

        video.dataset.mgDeferredVideo = '';
        content.appendChild(video);
      } else {
        const iframe = this.node('iframe', 'media-gallery-popup__embed');

        iframe.dataset.mgYoutube = '';
        iframe.dataset.src =
          `https://www.youtube-nocookie.com/embed/${youtube}?rel=0&playsinline=1`;

        iframe.title = titleText;
        iframe.allow =
          'autoplay; encrypted-media; picture-in-picture; fullscreen';
        iframe.allowFullscreen = true;

        content.appendChild(iframe);
      }

      return dialog;
    }

    prepareManualPhotos() {
      this.slides.forEach(slide => {
        const media = slide.querySelector('[data-mg-media]');
        if (!media) return;

        // Preserve linked images, video triggers, and inline players.
        if (media.querySelector('a, button, video, iframe')) return;

        const image = media.querySelector('img');
        if (!image) return;

        const trigger = this.node('button', 'media-gallery__trigger');
        trigger.type = 'button';
        trigger.dataset.mgPhotoOpen = '';
        trigger.dataset.mgPhotoUrl = image.src;
        trigger.dataset.mgPhotoTitle =
          slide.querySelector('.media-gallery__title')?.textContent.trim() ||
          image.alt || '';

        trigger.setAttribute(
          'aria-label',
          trigger.dataset.mgPhotoTitle || 'Enlarge photo'
        );

        image.replaceWith(trigger);
        trigger.appendChild(image);
      });
    }

    setupDelegatedControls() {
      this.listen(this, 'click', event => {
        const close = event.target.closest('[data-mg-popup-close]');

        if (close && this.contains(close)) {
          close.closest('dialog')?.close();
          return;
        }

        const photoNav = event.target.closest('[data-mg-photo-direction]');

        if (photoNav && this.contains(photoNav)) {
          this.showPhoto(
            this.photoIndex + Number(photoNav.dataset.mgPhotoDirection)
          );
          return;
        }

        const photo = event.target.closest('[data-mg-photo-open]');

        if (photo && this.contains(photo)) {
          this.openPhoto(photo);
          return;
        }

        const popup = event.target.closest('[data-mg-popup-open]');

        if (popup && this.contains(popup)) {
          const dialog = this.dialogs.find(
            item => item.id === popup.dataset.mgPopupOpen
          );

          if (dialog) this.openDialog(dialog, popup);
          return;
        }

        const inline = event.target.closest('[data-mg-inline-play]');

        if (inline && this.contains(inline)) {
          const media = inline.closest('[data-mg-media]');
          const iframe = media?.querySelector('iframe[data-mg-youtube]');

          if (!iframe) return;

          this.stopMedia();
          this.loadEmbed(iframe);
          iframe.hidden = false;
          inline.hidden = true;
          this.activeEmbed = iframe;
          iframe.focus();
          this.schedule();
        }
      });
    }

    bindDialog(dialog) {
      // A new controller is used on every gallery initialization.
      this.listen(dialog, 'click', event => {
        if (event.target === dialog) dialog.close();
      });

      this.listen(dialog, 'close', () => {
        dialog.querySelectorAll('video').forEach(video => video.pause());
        dialog.querySelectorAll('[data-mg-youtube]').forEach(
          iframe => this.unloadEmbed(iframe)
        );

        const trigger = dialog.mgReturnFocus;
        dialog.mgReturnFocus = null;

        if (trigger?.isConnected) {
          trigger.focus({ preventScroll: true });

          if (dialog.mgOpenedWithPointer) {
            trigger.blur();
          }
        }

        dialog.mgOpenedWithPointer = false;
        this.schedule();
      });
    }

    openDialog(dialog, trigger) {
      if (dialog.open) return;

      this.dialogs.forEach(other => {
        if (other.open) other.close();
      });

      this.stopMedia();

      dialog.mgReturnFocus = trigger;
      dialog.mgOpenedWithPointer = this.lastInteractionWasPointer === true;
      dialog.showModal();

      dialog.querySelectorAll('[data-mg-youtube]').forEach(
        iframe => this.loadEmbed(iframe)
      );

      dialog.querySelectorAll('[data-mg-deferred-video]').forEach(video => {
        if (video.dataset.mgVideoLoaded === 'true') return;

        video.querySelectorAll('source[data-src]').forEach(source => {
          source.src = source.dataset.src;
        });

        video.dataset.mgVideoLoaded = 'true';
        video.load();
      });

      this.schedule();
    }

    openPhoto(trigger) {
      this.photoItems = Array.from(
        this.track.querySelectorAll('[data-mg-photo-open]')
      );

      this.photoIndex = this.photoItems.indexOf(trigger);
      if (this.photoIndex < 0) return;

      let dialog = this.querySelector('[data-mg-photo-dialog]');

      if (!dialog) {
        dialog = this.createDialog(`${this.id}-PhotoLightbox`);
        dialog.dataset.mgPhotoDialog = '';

        const content = dialog.querySelector('.media-gallery-popup__content');
        const title = this.node('h2', 'media-gallery-popup__title');
        title.id = `${dialog.id}-Title`;
        title.dataset.mgPhotoTitle = '';
        dialog.setAttribute('aria-labelledby', title.id);

        const image = this.node('img');
        image.dataset.mgPhotoImage = '';
        image.style.cssText =
          'display:block;width:100%;height:auto;max-height:70vh;object-fit:contain;';

        const controls = this.node('div', 'media-gallery__autoscroll');
        controls.style.gap = '12px';

        const previous = this.node('button', 'media-gallery__pause', '←');
        previous.type = 'button';
        previous.dataset.mgPhotoDirection = '-1';
        previous.setAttribute('aria-label', 'Previous photo');

        const next = this.node('button', 'media-gallery__pause', '→');
        next.type = 'button';
        next.dataset.mgPhotoDirection = '1';
        next.setAttribute('aria-label', 'Next photo');

        controls.dataset.mgPhotoControls = '';
        controls.append(previous, next);

        content.append(title, image, controls);
        this.appendChild(dialog);
        this.bindDialog(dialog);

        this.listen(dialog, 'keydown', event => {
          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            this.showPhoto(this.photoIndex - 1);
          } else if (event.key === 'ArrowRight') {
            event.preventDefault();
            this.showPhoto(this.photoIndex + 1);
          }
        });
      }

      this.showPhoto(this.photoIndex);
      this.openDialog(dialog, trigger);
    }

    showPhoto(index) {
      if (!this.photoItems?.length) return;

      const count = this.photoItems.length;
      this.photoIndex = (index + count) % count;

      const item = this.photoItems[this.photoIndex];
      const dialog = this.querySelector('[data-mg-photo-dialog]');
      if (!dialog) return;

      const title = item.dataset.mgPhotoTitle || 'Photo';
      const image = dialog.querySelector('[data-mg-photo-image]');

      image.src = this.safeUrl(item.dataset.mgPhotoUrl);
      image.alt = title;

      const heading = dialog.querySelector('[data-mg-photo-title]');
      const hideTitle = item.dataset.mgPhotoHideTitle === 'true';

      heading.textContent = title;
      heading.hidden = hideTitle;

      if (hideTitle) {
        dialog.removeAttribute('aria-labelledby');
        dialog.setAttribute('aria-label', image.alt || 'Photo');
      } else {
        dialog.setAttribute('aria-labelledby', heading.id);
        dialog.removeAttribute('aria-label');
      }
      dialog.querySelector('[data-mg-photo-controls]').hidden = count < 2;
    }

    setupVideoEvents() {
      this.listen(this, 'play', event => {
        if (event.target.tagName !== 'VIDEO') return;

        this.querySelectorAll('video').forEach(video => {
          if (video !== event.target) video.pause();
        });

        this.schedule();
      }, { capture: true });

      this.listen(this, 'pause', () => this.schedule(), { capture: true });
      this.listen(this, 'ended', () => this.schedule(), { capture: true });
    }

    loadEmbed(iframe) {
      if (!iframe.dataset.src || iframe.hasAttribute('src')) return;
      iframe.src = iframe.dataset.src;
    }

    unloadEmbed(iframe) {
      iframe.removeAttribute('src');

      if (this.activeEmbed === iframe) this.activeEmbed = null;

      const media = iframe.closest('[data-mg-media]');
      const trigger = media?.querySelector('[data-mg-inline-play]');

      if (trigger) {
        iframe.hidden = true;
        trigger.hidden = false;
      }
    }

    stopMedia() {
      this.querySelectorAll('video').forEach(video => video.pause());
      this.querySelectorAll('[data-mg-youtube]').forEach(
        iframe => this.unloadEmbed(iframe)
      );
      this.activeEmbed = null;
    }

    pauseOffscreenMedia() {
      const trackRect = this.track.getBoundingClientRect();

      this.slides.forEach(slide => {
        const rect = slide.getBoundingClientRect();
        const visibleWidth = Math.max(
          0,
          Math.min(rect.right, trackRect.right) -
          Math.max(rect.left, trackRect.left)
        );

        if (
          visibleWidth >= Math.min(rect.width, trackRect.width) / 2
        ) {
          return;
        }

        slide.querySelectorAll('video').forEach(video => video.pause());
        slide.querySelectorAll('[data-mg-youtube]').forEach(
          iframe => this.unloadEmbed(iframe)
        );
      });
    }

    advance(direction, wrap = false) {
      const slides = this.slides;
      if (!slides.length || !this.hasOverflow) return;

      const maxScroll = this.track.scrollWidth - this.track.clientWidth;
      const current = this.track.scrollLeft;

      if (wrap && direction > 0 && current >= maxScroll - 2) {
        this.track.scrollTo({ left: 0, behavior: this.behavior });
        return;
      }

      const trackRect = this.track.getBoundingClientRect();

      const positions = slides.map(slide =>
        current + slide.getBoundingClientRect().left - trackRect.left
      );

      let target;

      if (direction > 0) {
        target = positions.find(position => position > current + 2);
        if (target === undefined) target = maxScroll;
      } else {
        target = positions.filter(position => position < current - 2).pop();
        if (target === undefined) target = 0;
      }

      this.track.scrollTo({
        left: Math.max(0, Math.min(maxScroll, target)),
        behavior: this.behavior
      });
    }

    updateNavigation() {
      const maxScroll = this.track.scrollWidth - this.track.clientWidth;

      if (this.prevButton) {
        this.prevButton.hidden = !this.hasOverflow;
        this.prevButton.disabled = this.track.scrollLeft <= 2;
      }

      if (this.nextButton) {
        this.nextButton.hidden = !this.hasOverflow;
        this.nextButton.disabled = this.track.scrollLeft >= maxScroll - 2;
      }

      const controls = this.querySelector('[data-mg-auto-controls]');

      if (controls) {
        controls.hidden = !this.autoEnabled || !this.hasOverflow;
      }
    }

    updatePauseButton() {
      if (!this.pauseButton) return;

      const text = this.userPaused
        ? this.pauseButton.dataset.resumeLabel || 'Resume auto-scroll'
        : this.pauseButton.dataset.pauseLabel || 'Pause auto-scroll';

      const label = this.pauseButton.querySelector('[data-mg-pause-label]');

      if (label) label.textContent = text;
      else this.pauseButton.textContent = text;

      this.pauseButton.setAttribute('aria-label', text);
      this.pauseButton.disabled = this.motionQuery.matches;
    }

    stopTimer() {
      clearTimeout(this.autoTimer);
      this.autoTimer = null;
    }

    schedule() {
      this.stopTimer();

      if (
        !this.isConnected ||
        !this.track ||
        this.signal?.aborted ||
        !this.autoEnabled ||
        this.userPaused ||
        this.motionQuery.matches ||
        !this.visible ||
        document.hidden ||
        this.hovered ||
        this.pointerDown ||
        this.contains(document.activeElement) ||
        this.popupOpen ||
        this.videoPlaying ||
        this.activeEmbed ||
        this.loadingGroup ||
        !this.hasOverflow
      ) {
        return;
      }

      this.autoTimer = setTimeout(() => {
        this.advance(1, true);
        this.schedule();
      }, this.interval);
    }

    setupEditorEvents() {
      this.listen(document, 'shopify:block:select', event => {
        const blockId = event.detail?.blockId;

        const slide = this.slides.find(
          item => item.dataset.blockId === blockId
        );

        if (!slide) return;

        this.userPaused = true;
        this.updatePauseButton();
        this.stopTimer();

        this.track.scrollTo({
          left: this.track.scrollLeft +
            slide.getBoundingClientRect().left -
            this.track.getBoundingClientRect().left,
          behavior: 'auto'
        });
      });
    }

    cleanup() {
      this.stopTimer();

      if (this.scrollFrame) {
        cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = null;
      }

      this.controller?.abort();
      this.resizeObserver?.disconnect();
      this.visibilityObserver?.disconnect();
      this.loadObserver?.disconnect();

      this.stopMedia();

      this.querySelectorAll('[data-mg-dialog]').forEach(dialog => {
        if (dialog.open) dialog.close();
      });
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.initFrame);
      this.cleanup();
    }
  }

  customElements.define(elementName, MediaGalleryCarousel);
})();